import React, { useContext, useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Container,
  Grid,
  Paper,
  Typography,
  Alert,
  Chip,
  Stack,
  CircularProgress,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  Autocomplete
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Event as EventIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AuthContext from '../../context/AuthContext';
import axios from 'axios';
import { getCoverArtUrl } from '../../utils/urlHelpers';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PlayLogPage = () => {
  const { user } = useContext(AuthContext);
  const [playLogs, setPlayLogs] = useState([]);
  const [library, setLibrary] = useState([]);
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [availableTracks, setAvailableTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    venueType: '',
    audienceReaction: '',
    dateFrom: null,
    dateTo: null
  });
  const [editingLog, setEditingLog] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [addDialog, setAddDialog] = useState(false);
  const [newLog, setNewLog] = useState({
    releaseId: '',
    trackId: '',
    playedAt: new Date(),
    venue: {
      name: '',
      location: { city: '', country: '' },
      type: 'club'
    },
    event: {
      name: '',
      description: '',
      attendees: '',
      duration: ''
    },
    notes: '',
    audienceReaction: 'good',
    tags: []
  });

  // Fetch play logs
  const fetchPlayLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        sortBy: 'playedAt',
        sortOrder: 'desc'
      });

      // Add filters to params
      if (filters.search) params.append('search', filters.search);
      if (filters.venueType) params.append('venueType', filters.venueType);
      if (filters.audienceReaction) params.append('audienceReaction', filters.audienceReaction);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom.toISOString());
      if (filters.dateTo) params.append('dateTo', filters.dateTo.toISOString());

      const response = await axios.get(`${API_URL}/play-logs/my-logs?${params}`);
      const { playLogs, pagination } = response.data;
      
      setPlayLogs(playLogs);
      setTotalPages(pagination.totalPages);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch play logs');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters]);

  // Fetch library for adding new play logs
  const fetchLibrary = async () => {
    try {
      const response = await axios.get(`${API_URL}/curator/library?limit=100&sortBy=savedAt&sortOrder=desc`);
      setLibrary(response.data.libraryEntries);
    } catch (err) {
    }
  };

  // Get unique releases from library
  const getUniqueReleases = () => {
    const releaseMap = new Map();
    library.forEach(item => {
      if (!releaseMap.has(item.release._id)) {
        releaseMap.set(item.release._id, item.release);
      }
    });
    return Array.from(releaseMap.values());
  };

  // Handle release selection
  const handleReleaseSelect = async (release) => {
    setSelectedRelease(release);
    if (release) {
      
      // Try to get tracks from the library first
      const tracksFromLibrary = library
        .filter(item => item.release._id === release._id && item.track)
        .map(item => item.track);
      
      let allTracks = [];
      
      // First, check if the release object itself has populated tracks
      if (release.tracks && release.tracks.length > 0) {
        if (typeof release.tracks[0] === 'string') {
          // Tracks are IDs, fetch from API or use library tracks if available
          if (tracksFromLibrary.length > 0) {
            allTracks = tracksFromLibrary;
          } else {
            // Fetch from API
            try {
              const trackPromises = release.tracks.map(trackId => 
                axios.get(`${API_URL}/tracks/${trackId}`)
              );
              const trackResponses = await Promise.all(trackPromises);
              allTracks = trackResponses.map(response => response.data);
            } catch (err) {
            }
          }
        } else {
          // Tracks are already populated objects
          allTracks = release.tracks;
        }
      } else if (tracksFromLibrary.length > 0) {
        allTracks = tracksFromLibrary;
      } else {
        // If no tracks found, fetch from API
        try {
          const response = await axios.get(`${API_URL}/releases/${release._id}`);
          const fetchedRelease = response.data;
          
          if (fetchedRelease.tracks && fetchedRelease.tracks.length > 0) {
            // Check if tracks are populated objects or just IDs
            if (typeof fetchedRelease.tracks[0] === 'string') {
              // Tracks are IDs, need to fetch track details
              const trackPromises = fetchedRelease.tracks.map(trackId => 
                axios.get(`${API_URL}/tracks/${trackId}`)
              );
              const trackResponses = await Promise.all(trackPromises);
              allTracks = trackResponses.map(response => response.data);
            } else {
              // Tracks are already populated objects
              allTracks = fetchedRelease.tracks;
            }
          }
        } catch (err) {
        }
      }
      
      // Filter out any invalid tracks and ensure they have proper structure
      allTracks = allTracks.filter(track => track && track._id && track.title);
      
      setAvailableTracks(allTracks);
      setNewLog({
        ...newLog,
        releaseId: release._id,
        trackId: '' // Reset track selection
      });
    } else {
      setAvailableTracks([]);
      setNewLog({
        ...newLog,
        releaseId: '',
        trackId: ''
      });
    }
  };

  // Handle track selection
  const handleTrackSelect = (track) => {
    
    if (track) {
      // Always allow track selection if it's available
      setNewLog({
        ...newLog,
        trackId: track._id
      });
    } else {
      setNewLog({
        ...newLog,
        trackId: ''
      });
    }
  };

  // Reset form when dialog is closed
  const resetForm = () => {
    setSelectedRelease(null);
    setAvailableTracks([]);
    setNewLog({
      releaseId: '',
      trackId: '',
      playedAt: new Date(),
      venue: {
        name: '',
        location: { city: '', country: '' },
        type: 'club'
      },
      event: {
        name: '',
        description: '',
        attendees: '',
        duration: ''
      },
      notes: '',
      audienceReaction: 'good',
      tags: []
    });
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/play-logs/stats`);
      setStats(response.data);
    } catch (err) {
    }
  };

  useEffect(() => {
    if (user && user.accountType === 'curator') {
      fetchPlayLogs();
      fetchLibrary();
      fetchStats();
    }
  }, [user, fetchPlayLogs]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1);
  };

  // Handle add play log
  const handleAddLog = async () => {
    try {
      
      // Validate required fields
      if (!newLog.releaseId || !newLog.venue.name || !newLog.venue.type) {
        setError('Please fill in all required fields');
        return;
      }
      
      await axios.post(`${API_URL}/play-logs`, newLog);
      setAddDialog(false);
      resetForm();
      fetchPlayLogs();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add play log');
    }
  };

  // Handle edit log
  const handleEdit = (log) => {
    setEditingLog(log);
    setEditDialog(true);
  };

  // Save edited log
  const saveEdit = async () => {
    try {
      await axios.put(`${API_URL}/play-logs/${editingLog._id}`, editingLog);
      setEditDialog(false);
      fetchPlayLogs();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update play log');
    }
  };

  // Delete play log
  const deleteLog = async (logId) => {
    try {
      await axios.delete(`${API_URL}/play-logs/${logId}`);
      fetchPlayLogs();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete play log');
    }
  };

  if (user?.accountType !== 'curator') {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          This page is only accessible to curator accounts.
        </Alert>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ color: '#e4e4e4' }}>
            Play Logs
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track when and where you played your music
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Stats Overview */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="primary">
                {stats.totalPlays || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Plays
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">
                {stats.uniqueReleases || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unique Releases
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="error.main">
                {stats.uniqueVenues || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unique Venues
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="info.main">
                {stats.recentActivity || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Recent (30d)
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search"
                variant="outlined"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#666666' },
                    '&:hover fieldset': { borderColor: '#666666' },
                    '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#666666' },
                  '&:hover fieldset': { borderColor: '#666666' },
                  '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                }
              }}>
                <InputLabel>Venue Type</InputLabel>
                <Select
                  value={filters.venueType}
                  label="Venue Type"
                  onChange={(e) => handleFilterChange('venueType', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="club">Club</MenuItem>
                  <MenuItem value="festival">Festival</MenuItem>
                  <MenuItem value="radio">Radio</MenuItem>
                  <MenuItem value="podcast">Podcast</MenuItem>
                  <MenuItem value="livestream">Livestream</MenuItem>
                  <MenuItem value="private_event">Private Event</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#666666' },
                  '&:hover fieldset': { borderColor: '#666666' },
                  '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                }
              }}>
                <InputLabel>Reaction</InputLabel>
                <Select
                  value={filters.audienceReaction}
                  label="Reaction"
                  onChange={(e) => handleFilterChange('audienceReaction', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="excellent">Excellent</MenuItem>
                  <MenuItem value="good">Good</MenuItem>
                  <MenuItem value="average">Average</MenuItem>
                  <MenuItem value="poor">Poor</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <DatePicker
                label="From Date"
                value={filters.dateFrom}
                onChange={(date) => handleFilterChange('dateFrom', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#666666' },
                        '&:hover fieldset': { borderColor: '#666666' },
                        '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                      }
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <DatePicker
                label="To Date"
                value={filters.dateTo}
                onChange={(date) => handleFilterChange('dateTo', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#666666' },
                        '&:hover fieldset': { borderColor: '#666666' },
                        '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                      }
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Play Logs */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2}>
            {playLogs.map((log) => (
              <Card key={log._id}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={2}>
                      <CardMedia
                        component="img"
                        height="100"
                        image={getCoverArtUrl(log.release.coverArt)}
                        alt={log.release.title}
                        sx={{ borderRadius: 1 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Typography variant="h6" gutterBottom>
                        {log.release.title}
                      </Typography>
                      {log.track && (
                        <Typography variant="body1" color="primary" gutterBottom>
                          Track: {log.track.title}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        by {log.release.artist?.artistName || log.release.artist?.username}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <EventIcon sx={{ mr: 1, fontSize: 16 }} />
                        <Typography variant="body2">
                          {new Date(log.playedAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <LocationIcon sx={{ mr: 1, fontSize: 16 }} />
                        <Typography variant="body2">
                          {log.venue.name}
                          {log.venue.location?.city && `, ${log.venue.location.city}`}
                          {log.venue.location?.country && `, ${log.venue.location.country}`}
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        <Chip
                          label={log.venue.type}
                          size="small"
                          color="primary"
                        />
                        <Chip
                          label={log.audienceReaction}
                          size="small"
                          color={log.audienceReaction === 'excellent' ? 'success' : 'default'}
                        />
                        {log.event?.attendees && (
                          <Chip
                            label={`${log.event.attendees} people`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Stack>

                      {log.notes && (
                        <Typography variant="body2" color="text.secondary">
                          {log.notes}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <IconButton color="primary" onClick={() => handleEdit(log)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton color="error" onClick={() => deleteLog(log._id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(event, value) => setCurrentPage(value)}
              color="primary"
            />
          </Box>
        )}

        {/* Add FAB */}
        <Fab
          color="primary"
          aria-label="add"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={() => setAddDialog(true)}
        >
          <AddIcon />
        </Fab>

        {/* Add Dialog */}
        <Dialog open={addDialog} onClose={() => { setAddDialog(false); resetForm(); }} maxWidth="md" fullWidth>
          <DialogTitle>Add Play Log</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={getUniqueReleases()}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    const artistName = option.artist?.artistName || option.artist?.username || 'Unknown Artist';
                    return `${option.title || 'Untitled'} - ${artistName}`;
                  }}
                  value={selectedRelease}
                  onChange={(event, newValue) => handleReleaseSelect(newValue)}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return option._id === value._id;
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Release" fullWidth sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#666666' },
                        '&:hover fieldset': { borderColor: '#666666' },
                        '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                      }
                    }} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={availableTracks.filter(track => track && track._id && track.title)}
                  getOptionLabel={(option) => {
                    // Handle the case where option might be a string (ID) instead of object
                    if (typeof option === 'string') {
                      const trackObj = availableTracks.find(t => t._id === option);
                      return trackObj ? trackObj.title : '';
                    }
                    // Ensure we have a valid track object
                    if (!option || typeof option !== 'object' || !option.title) {
                      return '';
                    }
                    return option.title;
                  }}
                  value={availableTracks.find(track => track && track._id === newLog.trackId) || null}
                  onChange={(event, newValue) => handleTrackSelect(newValue)}
                  disabled={!selectedRelease || availableTracks.length === 0}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    // Handle string IDs vs objects
                    const optionId = typeof option === 'string' ? option : option._id;
                    const valueId = typeof value === 'string' ? value : value._id;
                    return optionId === valueId;
                  }}
                  noOptionsText={selectedRelease ? "No tracks available for this release" : "Select a release first"}
                  renderInput={(params) => (
                    <TextField {...params} 
                      label="Select Track (Optional)" 
                      fullWidth 
                      helperText={availableTracks.length > 0 ? "Leave blank to log entire release" : "No tracks available for this release"}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: '#666666' },
                          '&:hover fieldset': { borderColor: '#666666' },
                          '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                        }
                      }} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Played Date"
                  value={newLog.playedAt}
                  onChange={(date) => setNewLog({...newLog, playedAt: date})}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: '#666666' },
                          '&:hover fieldset': { borderColor: '#666666' },
                          '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                        }
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Venue Name"
                  value={newLog.venue.name}
                  onChange={(e) => setNewLog({
                    ...newLog,
                    venue: { ...newLog.venue, name: e.target.value }
                  })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#666666' },
                      '&:hover fieldset': { borderColor: '#666666' },
                      '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={newLog.venue.location.city}
                  onChange={(e) => setNewLog({
                    ...newLog,
                    venue: {
                      ...newLog.venue,
                      location: { ...newLog.venue.location, city: e.target.value }
                    }
                  })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#666666' },
                      '&:hover fieldset': { borderColor: '#666666' },
                      '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country"
                  value={newLog.venue.location.country}
                  onChange={(e) => setNewLog({
                    ...newLog,
                    venue: {
                      ...newLog.venue,
                      location: { ...newLog.venue.location, country: e.target.value }
                    }
                  })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#666666' },
                      '&:hover fieldset': { borderColor: '#666666' },
                      '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#666666' },
                    '&:hover fieldset': { borderColor: '#666666' },
                    '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                  }
                }}>
                  <InputLabel>Venue Type</InputLabel>
                  <Select
                    value={newLog.venue.type}
                    label="Venue Type"
                    onChange={(e) => setNewLog({
                      ...newLog,
                      venue: { ...newLog.venue, type: e.target.value }
                    })}
                  >
                    <MenuItem value="club">Club</MenuItem>
                    <MenuItem value="festival">Festival</MenuItem>
                    <MenuItem value="radio">Radio</MenuItem>
                    <MenuItem value="podcast">Podcast</MenuItem>
                    <MenuItem value="livestream">Livestream</MenuItem>
                    <MenuItem value="private_event">Private Event</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#666666' },
                    '&:hover fieldset': { borderColor: '#666666' },
                    '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                  }
                }}>
                  <InputLabel>Audience Reaction</InputLabel>
                  <Select
                    value={newLog.audienceReaction}
                    label="Audience Reaction"
                    onChange={(e) => setNewLog({...newLog, audienceReaction: e.target.value})}
                  >
                    <MenuItem value="excellent">Excellent</MenuItem>
                    <MenuItem value="good">Good</MenuItem>
                    <MenuItem value="average">Average</MenuItem>
                    <MenuItem value="poor">Poor</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Event Name"
                  value={newLog.event.name}
                  onChange={(e) => setNewLog({
                    ...newLog,
                    event: { ...newLog.event, name: e.target.value }
                  })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#666666' },
                      '&:hover fieldset': { borderColor: '#666666' },
                      '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={newLog.notes}
                  onChange={(e) => setNewLog({...newLog, notes: e.target.value})}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#666666' },
                      '&:hover fieldset': { borderColor: '#666666' },
                      '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddLog} variant="contained">Add</Button>
          </DialogActions>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Edit Play Log</DialogTitle>
          <DialogContent>
            {editingLog && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Played Date"
                    value={new Date(editingLog.playedAt)}
                    onChange={(date) => setEditingLog({...editingLog, playedAt: date})}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#666666' },
                            '&:hover fieldset': { borderColor: '#666666' },
                            '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                          }
                        }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Venue Name"
                    value={editingLog.venue.name}
                    onChange={(e) => setEditingLog({
                      ...editingLog,
                      venue: { ...editingLog.venue, name: e.target.value }
                    })}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#666666' },
                        '&:hover fieldset': { borderColor: '#666666' },
                        '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#666666' },
                      '&:hover fieldset': { borderColor: '#666666' },
                      '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                    }
                  }}>
                    <InputLabel>Venue Type</InputLabel>
                    <Select
                      value={editingLog.venue.type}
                      label="Venue Type"
                      onChange={(e) => setEditingLog({
                        ...editingLog,
                        venue: { ...editingLog.venue, type: e.target.value }
                      })}
                    >
                      <MenuItem value="club">Club</MenuItem>
                      <MenuItem value="festival">Festival</MenuItem>
                      <MenuItem value="radio">Radio</MenuItem>
                      <MenuItem value="podcast">Podcast</MenuItem>
                      <MenuItem value="livestream">Livestream</MenuItem>
                      <MenuItem value="private_event">Private Event</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#666666' },
                      '&:hover fieldset': { borderColor: '#666666' },
                      '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                    }
                  }}>
                    <InputLabel>Audience Reaction</InputLabel>
                    <Select
                      value={editingLog.audienceReaction}
                      label="Audience Reaction"
                      onChange={(e) => setEditingLog({...editingLog, audienceReaction: e.target.value})}
                    >
                      <MenuItem value="excellent">Excellent</MenuItem>
                      <MenuItem value="good">Good</MenuItem>
                      <MenuItem value="average">Average</MenuItem>
                      <MenuItem value="poor">Poor</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={3}
                    value={editingLog.notes || ''}
                    onChange={(e) => setEditingLog({...editingLog, notes: e.target.value})}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#666666' },
                        '&:hover fieldset': { borderColor: '#666666' },
                        '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                      }
                    }}
                  />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={saveEdit} variant="contained">Save</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default PlayLogPage;
